import React from 'react';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from './ui/tooltip';

/**
 * HaloRing — Iter-213+ — StaffConnect-style BOLD operational status ring
 * wrapped around profile photos / avatars. Designed to be visible at a glance
 * on both desktop and mobile.
 *
 * Severity precedence (highest wins): red > amber > green > gray.
 *
 * Implementation note: Tailwind's `ring-N` utilities render via box-shadow,
 * which is too subtle on white cards. We use a real CSS border + a layered
 * outer halo (box-shadow with rgba glow) so the ring stands out clearly on
 * white backgrounds and is unmistakable on a phone screen.
 *
 * Usage:
 *   <HaloRing state="red" reason="Not checked in — shift started" size="md">
 *     <Avatar>...</Avatar>
 *   </HaloRing>
 *
 * Helper:
 *   HaloRing.computeFromBooking(booking, { shiftActive: bool }) // → { state, reason }
 */

// Bold, mobile-visible color palette. Border carries the dominant color;
// the box-shadow glow adds a 4-6px soft outer halo so the ring reads even
// against busy backgrounds. Inline style keeps Tailwind out of the way for
// the exact widths we want (clean 4-6px instead of `ring-4`'s blurry 4px).
const STATE_STYLES = {
  red: {
    border: '#ef4444',        // rose-500
    glow:   'rgba(244,63,94,0.45)',
    label:  'Risk — needs attention',
  },
  amber: {
    border: '#f59e0b',        // amber-500
    glow:   'rgba(245,158,11,0.45)',
    label:  'Pending — review needed',
  },
  green: {
    border: '#10b981',        // emerald-500
    glow:   'rgba(16,185,129,0.45)',
    label:  'All clear',
  },
  gray: {
    border: '#d4d4d8',        // zinc-300
    glow:   'transparent',
    label:  'Not applicable',
  },
};

const SIZE_PX = {
  sm: 3,
  md: 4,
  lg: 5,
};

const HaloRing = ({
  state = 'gray',
  reason = null,
  size = 'md',          // sm | md | lg
  children,
  testIdSuffix = '',
}) => {
  const s = STATE_STYLES[state] || STATE_STYLES.gray;
  const borderPx = SIZE_PX[size] ?? SIZE_PX.md;
  const haloPx = borderPx + 2;

  const style = {
    border: `${borderPx}px solid ${s.border}`,
    // Layered outer halo for the bright/glow effect. Gray gets no glow so
    // it stays understated. Other states get a punchy ring of color.
    boxShadow: state === 'gray'
      ? 'none'
      : `0 0 0 1px rgba(255,255,255,0.9), 0 0 0 ${haloPx}px ${s.glow}`,
    background: '#ffffff', // ensures a clean white gap between border and inner photo on dark cards
    padding: 0,
    display: 'inline-block',
    lineHeight: 0,
    flex: 'none',
  };

  const node = (
    <span
      className="rounded-full"
      style={style}
      data-testid={`halo-ring${testIdSuffix ? '-' + testIdSuffix : ''}`}
      data-halo-state={state}
    >
      <span className="block rounded-full overflow-hidden">{children}</span>
    </span>
  );

  if (!reason && state === 'gray') return node;

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>{node}</TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <p className="text-xs font-semibold mb-0.5">{s.label}</p>
          {reason && <p className="text-xs text-gray-300">{reason}</p>}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/**
 * Compute a halo state + reason from a booking-like object.
 *
 * Optional `opts.shiftActive` (default false): when true, treats a confirmed
 * booking with NO check-in as RED (because the shift is currently in progress
 * and they haven't shown up yet). On the Attendance & Compliance panel this
 * is the canonical "ops" interpretation — RED until checked in.
 *
 * Recognises booking fields:
 *   status:                 'confirmed' | 'pending_confirmation' | 'no_show' | 'completed' | 'cancelled'
 *   checkin_verification:   enum (pending, approved_gps_only, rejected_by_admin, ...)
 *   checkin_time:           ISO datetime or null
 *   selfie_failed / checkout_selfie_failed: bool
 *   needs_review:           bool
 *   halo_state:             prefilled by backend (V162 column)
 */
const computeFromBooking = (b, opts = {}) => {
  if (!b || typeof b !== 'object') return { state: 'gray', reason: null };
  const shiftActive = !!opts.shiftActive;

  // Prefer backend-computed state when available (Iter-214 wires the trigger;
  // until then this branch is a no-op since defaults are 'gray').
  if (b.halo_state && b.halo_state !== 'gray') {
    return { state: b.halo_state, reason: b.halo_reason_code || null };
  }

  const verif = (b.checkin_verification || '').toLowerCase();
  const status = (b.status || '').toLowerCase();
  // The /attendance/overview/{shift_id} payload uses `attendance_status` instead
  // of raw booking status. Treat it as the primary signal on attendance surfaces
  // (checked_in / checked_out / not_checked_in / no_show).
  const att = (b.attendance_status || '').toLowerCase();
  const hasCheckedIn = !!(b.checkin_time || b.clock_in_time) || att === 'checked_in' || att === 'checked_out';
  const hasCheckedOut = !!(b.checkout_time || b.clock_out_time) || att === 'checked_out';

  // Gray: cancelled
  if (status === 'cancelled') {
    return { state: 'gray', reason: 'Cancelled' };
  }

  // Red: high-risk, needs immediate attention
  if (status === 'no_show' || att === 'no_show') return { state: 'red', reason: 'Marked no-show' };
  if (verif === 'rejected_by_admin') return { state: 'red', reason: 'Check-in rejected by admin' };
  if (verif === 'pending_correction') return { state: 'red', reason: 'Awaiting talent correction' };
  if (verif === 'selfie_failed' || b.selfie_failed) return { state: 'red', reason: 'Selfie verification failed' };
  if (b.checkout_selfie_failed) return { state: 'red', reason: 'Check-out selfie failed' };
  // Ops view: shift is active right now and they haven't clocked in → RED.
  if (shiftActive && !hasCheckedIn && (att === 'not_checked_in' || status === 'confirmed')) {
    return { state: 'red', reason: 'Not checked in — shift active' };
  }
  // Attendance panel: explicit "not_checked_in" status when shift_active wasn't
  // passed in — still surface as RED so ops can see who's missing at a glance.
  // (The panel always passes shiftActive, so this is the fallback for stale views.)
  if (att === 'not_checked_in' && !shiftActive) {
    return { state: 'red', reason: 'Not checked in' };
  }

  // Amber: in progress / awaiting
  if (verif === 'pending') return { state: 'amber', reason: 'Awaiting admin review' };
  if (verif === 'gps_only_review' || verif === 'outside_radius_review') {
    return { state: 'amber', reason: 'GPS-only — awaiting admin approval' };
  }
  if (verif === 'gps_warning') return { state: 'amber', reason: 'GPS distance warning' };
  if (b.needs_review) return { state: 'amber', reason: 'Flagged for review' };
  if (status === 'pending_confirmation') return { state: 'amber', reason: 'Awaiting talent confirmation' };
  // Clocked in but no clock-out yet → amber (in progress).
  if (hasCheckedIn && !hasCheckedOut && att !== 'checked_out') {
    return { state: 'amber', reason: 'Clocked in — shift in progress' };
  }

  // Green: confirmed + verified, or wrapped up cleanly
  const GREEN_VERIFS = new Set([
    'approved', 'auto_approved', 'verified',
    'approved_gps_only', 'approved_by_admin', 'gps_verified',
  ]);
  if (att === 'checked_out' || hasCheckedOut) {
    return { state: 'green', reason: 'Checked out' };
  }
  if (status === 'confirmed' && GREEN_VERIFS.has(verif)) {
    return { state: 'green', reason: 'Confirmed & verified' };
  }
  if (status === 'completed' && hasCheckedIn) {
    return { state: 'green', reason: 'Shift completed' };
  }
  if (status === 'completed') {
    return { state: 'gray', reason: 'Shift wrapped' };
  }
  if (status === 'confirmed') return { state: 'green', reason: 'Confirmed' };

  return { state: 'gray', reason: null };
};

HaloRing.computeFromBooking = computeFromBooking;
HaloRing.STATES = ['red', 'amber', 'green', 'gray'];

export default HaloRing;
export { computeFromBooking };
